import React, { useState, useRef, useEffect } from 'react';
import { 
  MessageCircle, 
  X, 
  Send, 
  Download, 
  Save, 
  Minimize2, 
  Maximize2,
  Target,
  BookOpen,
  MessageSquare,
  Loader,
  AlertCircle,
  CheckCircle,
  Move
} from 'lucide-react';
import { chatTypes } from '../data/chatData';
import deepseekChatService from '../services/deepseekChatApi';

const FloatingChatWindow = ({ 
  isOpen, 
  onClose, 
  context = {}, 
  onSaveAsQuizlet, 
  onSaveAsDrill,
  onReturnToTopic 
}) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [activeTab, setActiveTab] = useState('selection');
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [currentChatType, setCurrentChatType] = useState(null);
  const [savedContent, setSavedContent] = useState(null);
  const [showSaveOptions, setShowSaveOptions] = useState(false);
  
  // Dragging state
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 50, y: 50 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const windowRef = useRef(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && activeTab === 'chat' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, activeTab]);

  // Handle mouse/touch events for dragging
  const handleMouseDown = (e) => {
    if (e.target.closest('button') || e.target.closest('input')) return;
    
    setIsDragging(true);
    const rect = windowRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const newX = e.clientX - dragOffset.x;
    const newY = e.clientY - dragOffset.y;
    
    // Keep window within viewport bounds
    const maxX = window.innerWidth - (isMinimized ? 320 : 384);
    const maxY = window.innerHeight - (isMinimized ? 64 : 600);
    
    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY))
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch event handlers for better tablet support
  const handleTouchStart = (e) => {
    if (e.target.closest('button') || e.target.closest('input')) return;
    
    setIsDragging(true);
    const touch = e.touches[0];
    const rect = windowRef.current.getBoundingClientRect();
    setDragOffset({
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    });
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    e.preventDefault(); // Prevent scrolling while dragging
    
    const touch = e.touches[0];
    const newX = touch.clientX - dragOffset.x;
    const newY = touch.clientY - dragOffset.y;
    
    // Keep window within viewport bounds
    const maxX = window.innerWidth - (isMinimized ? 320 : 384);
    const maxY = window.innerHeight - (isMinimized ? 64 : 600);
    
    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY))
    });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Add global event listeners
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isOpen, isDragging, dragOffset, isMinimized]);

  // Handle chat type selection
  const handleChatTypeSelection = async (chatType) => {
    setCurrentChatType(chatType);
    setActiveTab('chat');
    setIsLoading(true);
    
    const topic = context.title || context.topic || 'English Grammar';
    const initialMessage = {
      id: Date.now(),
      type: 'user',
      content: `Start ${chatType.name} for ${topic}`,
      timestamp: new Date().toISOString()
    };
    
    setMessages([initialMessage]);
    
    try {
      let response;
      
      switch (chatType.id) {
        case 'quick-quiz':
          response = await deepseekChatService.generateQuickQuiz(topic, context);
          break;
        case 'drill-exercise':
          response = await deepseekChatService.generateDrillExercise(topic, context);
          break;
        case 'direct-chat':
          response = await deepseekChatService.continueChat([initialMessage], context);
          break;
        default:
          response = { success: false, error: 'Unknown chat type' };
      }
      
      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: response.success ? response.content : response.fallbackContent,
        timestamp: new Date().toISOString(),
        isError: !response.success
      };
      
      setMessages(prev => [...prev, aiMessage]);
      
      if (response.success) {
        setSavedContent({
          type: chatType.id,
          content: response.content,
          topic,
          context
        });
      }
      
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: `Sorry, I encountered an error: ${error.message}. Please try again.`,
        timestamp: new Date().toISOString(),
        isError: true
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle sending a message
  const handleSendMessage = async () => {
    if (!currentMessage.trim() || isLoading) return;
    
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: currentMessage,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setCurrentMessage('');
    setIsLoading(true);
    
    try {
      const response = await deepseekChatService.continueChat(
        [...messages, userMessage],
        context
      );
      
      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: response.success ? response.content : response.fallbackContent,
        timestamp: new Date().toISOString(),
        isError: !response.success
      };
      
      setMessages(prev => [...prev, aiMessage]);
      
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: `Sorry, I encountered an error: ${error.message}. Please try again.`,
        timestamp: new Date().toISOString(),
        isError: true
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle key press in input
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Download conversation
  const handleDownload = () => {
    const content = messages.map(msg => 
      `${msg.type === 'user' ? 'You' : 'AI Tutor'}: ${msg.content}`
    ).join('\n\n');
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chat-${context.title || 'conversation'}-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Save as Quizlet
  const handleSaveAsQuizlet = () => {
    if (savedContent && currentChatType?.id === 'quick-quiz') {
      const questions = deepseekChatService.parseQuizResponse(savedContent.content);
      onSaveAsQuizlet({
        title: `${savedContent.topic} Quiz`,
        questions,
        contextId: context.id,
        createdAt: new Date().toISOString()
      });
      setShowSaveOptions(false);
    }
  };

  // Save as Drill
  const handleSaveAsDrill = () => {
    if (savedContent && currentChatType?.id === 'drill-exercise') {
      const drillData = deepseekChatService.parseDrillResponse(savedContent.content);
      onSaveAsDrill({
        title: `${savedContent.topic} Drill`,
        ...drillData,
        contextId: context.id,
        createdAt: new Date().toISOString()
      });
      setShowSaveOptions(false);
    }
  };

  // Reset chat
  const resetChat = () => {
    setActiveTab('selection');
    setMessages([]);
    setCurrentMessage('');
    setCurrentChatType(null);
    setSavedContent(null);
    setShowSaveOptions(false);
  };

  // Return to topic
  const handleReturnToTopic = () => {
    onReturnToTopic();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      ref={windowRef}
      className={`fixed z-50 transition-all duration-300 ${
        isDragging ? 'select-none' : ''
      }`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: isMinimized ? '320px' : '384px',
        height: isMinimized ? '64px' : '600px',
        maxWidth: '90vw',
        maxHeight: '90vh'
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      <div className={`bg-white rounded-lg shadow-2xl border-2 border-gray-200 h-full flex flex-col ${
        isDragging ? 'shadow-2xl' : 'shadow-lg'
      }`}>
        {/* Header */}
        <div 
          className={`flex items-center justify-between p-4 border-b bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-t-lg cursor-move ${
            isDragging ? 'bg-opacity-90' : ''
          }`}
          style={{ touchAction: 'none' }}
        >
          <div className="flex items-center space-x-2">
            <Move className="w-4 h-4 opacity-70" />
            <MessageCircle className="w-5 h-5" />
            <h3 className="font-semibold">
              {context.title ? `AI Tutor - ${context.title}` : 'AI Tutor'}
            </h3>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
              style={{ touchAction: 'manipulation' }}
            >
              {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
            </button>
            <button
              onClick={onClose}
              className="p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
              style={{ touchAction: 'manipulation' }}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        {!isMinimized && (
          <div className="flex flex-col flex-1 overflow-hidden">
            {/* Chat Type Selection */}
            {activeTab === 'selection' && (
              <div className="flex-1 p-4 space-y-4 overflow-y-auto">
                <div className="text-center mb-4">
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">
                    How can I help you today?
                  </h4>
                  <p className="text-sm text-gray-600">
                    Choose how you'd like to learn about {context.title || 'this topic'}
                  </p>
                </div>
                
                <div className="space-y-3">
                  {Object.values(chatTypes).map(chatType => (
                    <button
                      key={chatType.id}
                      onClick={() => handleChatTypeSelection(chatType)}
                      className={`w-full p-4 rounded-lg border-2 border-gray-200 hover:border-blue-300 transition-colors text-left ${chatType.color} hover:bg-opacity-10`}
                      style={{ touchAction: 'manipulation' }}
                    >
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">{chatType.icon}</span>
                        <div>
                          <h5 className="font-semibold text-gray-800">{chatType.name}</h5>
                          <p className="text-sm text-gray-600">{chatType.description}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Chat Interface */}
            {activeTab === 'chat' && (
              <>
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messages.map(message => (
                    <div
                      key={message.id}
                      className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] p-3 rounded-lg ${
                          message.type === 'user'
                            ? 'bg-blue-500 text-white'
                            : message.isError
                            ? 'bg-red-50 text-red-800 border border-red-200'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {message.isError && (
                          <div className="flex items-center space-x-2 mb-2">
                            <AlertCircle className="w-4 h-4" />
                            <span className="text-sm font-medium">Error</span>
                          </div>
                        )}
                        <div className="whitespace-pre-wrap text-sm">
                          {message.content}
                        </div>
                        <div className={`text-xs mt-1 ${
                          message.type === 'user' ? 'text-blue-100' : 'text-gray-500'
                        }`}>
                          {new Date(message.timestamp).toLocaleTimeString()}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {isLoading && (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 p-3 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <Loader className="w-4 h-4 animate-spin" />
                          <span className="text-sm text-gray-600">AI is thinking...</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="border-t p-4 space-y-3">
                  {/* Action Buttons */}
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={handleDownload}
                        className="flex items-center space-x-1 px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded text-sm transition-colors"
                        disabled={messages.length === 0}
                        style={{ touchAction: 'manipulation' }}
                      >
                        <Download className="w-4 h-4" />
                        <span>Download</span>
                      </button>
                      
                      {savedContent && (
                        <button
                          onClick={() => setShowSaveOptions(!showSaveOptions)}
                          className="flex items-center space-x-1 px-3 py-1 bg-green-100 hover:bg-green-200 rounded text-sm transition-colors"
                          style={{ touchAction: 'manipulation' }}
                        >
                          <Save className="w-4 h-4" />
                          <span>Save</span>
                        </button>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={resetChat}
                        className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded text-sm transition-colors"
                        style={{ touchAction: 'manipulation' }}
                      >
                        New Chat
                      </button>
                      
                      <button
                        onClick={handleReturnToTopic}
                        className="px-3 py-1 bg-blue-100 hover:bg-blue-200 rounded text-sm transition-colors"
                        style={{ touchAction: 'manipulation' }}
                      >
                        Return to Topic
                      </button>
                    </div>
                  </div>

                  {/* Save Options */}
                  {showSaveOptions && savedContent && (
                    <div className="bg-gray-50 p-3 rounded-lg space-y-2">
                      <p className="text-sm font-medium text-gray-700">Save as:</p>
                      <div className="flex space-x-2">
                        {currentChatType?.id === 'quick-quiz' && (
                          <button
                            onClick={handleSaveAsQuizlet}
                            className="flex items-center space-x-1 px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 transition-colors"
                            style={{ touchAction: 'manipulation' }}
                          >
                            <Target className="w-4 h-4" />
                            <span>Quizlet</span>
                          </button>
                        )}
                        
                        {currentChatType?.id === 'drill-exercise' && (
                          <button
                            onClick={handleSaveAsDrill}
                            className="flex items-center space-x-1 px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600 transition-colors"
                            style={{ touchAction: 'manipulation' }}
                          >
                            <BookOpen className="w-4 h-4" />
                            <span>Drill</span>
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Message Input */}
                  {currentChatType?.id === 'direct-chat' && (
                    <div className="flex items-center space-x-2">
                      <input
                        ref={inputRef}
                        type="text"
                        value={currentMessage}
                        onChange={(e) => setCurrentMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Type your message..."
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={isLoading}
                        style={{ touchAction: 'manipulation' }}
                      />
                      <button
                        onClick={handleSendMessage}
                        disabled={!currentMessage.trim() || isLoading}
                        className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        style={{ touchAction: 'manipulation' }}
                      >
                        <Send className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FloatingChatWindow; 